import React, { useState } from 'react';
import { getTheme, useMobile } from '../utils/theme';
import { Download, Smartphone, MapPin, Activity, Shield, ChevronDown, ChevronUp, CheckCircle } from 'lucide-react';

// App icon as inline SVG (matches the generated icon)
function AppIcon({ size = 80 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 1024 1024" xmlns="http://www.w3.org/2000/svg">
      <rect width="1024" height="1024" rx="184" fill="#1a1a2e" />
      {/* Pin body */}
      <path d="M512 122 C370 122 225 240 225 400 C225 500 350 680 512 870 C674 680 799 500 799 400 C799 240 654 122 512 122 Z" fill="#f97316" />
      {/* Inner circle */}
      <circle cx="512" cy="400" r="150" fill="#1a1a2e" />
      {/* L letter */}
      <text x="512" y="420" textAnchor="middle" dominantBaseline="middle" fill="white" fontWeight="900" fontSize="225" fontFamily="Arial, Helvetica, sans-serif">L</text>
      {/* Signal waves */}
      <path d="M700 280 Q740 260 750 300" stroke="#f97316" strokeWidth="18" fill="none" strokeLinecap="round" />
      <path d="M720 240 Q780 210 795 270" stroke="#f97316" strokeWidth="18" fill="none" strokeLinecap="round" />
      <path d="M740 200 Q820 160 840 240" stroke="#f97316" strokeWidth="18" fill="none" strokeLinecap="round" />
    </svg>
  );
}

export default function FieldAppPage({ darkMode, setDarkMode }) {
  const theme = getTheme(darkMode);
  const isMobile = useMobile();
  const [showInstructions, setShowInstructions] = useState(false);

  const APK_URL = 'https://github.com/MSRCAM83/lyt-field/releases/download/v1.0.0/lyt-field.apk';

  const features = [
    { icon: MapPin, title: 'Auto-Track Progress', desc: 'GPS detects when you arrive at and complete segments — no manual check-ins needed.' },
    { icon: Activity, title: 'Smart Detection', desc: 'Learns your work patterns over time. Gets smarter every day you use it.' },
    { icon: Smartphone, title: 'Works Offline', desc: 'Full functionality even without signal. Syncs automatically when connected.' },
    { icon: Shield, title: 'Daily Review', desc: 'Quick end-of-day confirmation. The app does the work, you just approve.' },
  ];

  const steps = [
    { num: '1', text: 'Tap the download button below on your Android phone' },
    { num: '2', text: 'When prompted, tap "Download anyway" (it\'s not from the Play Store yet)' },
    { num: '3', text: 'Open the downloaded file (check your notifications or Downloads folder)' },
    { num: '4', text: 'Tap "Install" — you may need to allow installs from this source' },
    { num: '5', text: 'Open LYT Field and sign in with your LYT email and password' },
    { num: '6', text: 'Allow location access (both "While Using" and "Always") for auto-tracking' },
  ];

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: darkMode ? '#0f172a' : '#f8fafc',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
    }}>
      {/* Hero */}
      <div style={{
        background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        padding: isMobile ? '48px 20px 40px' : '64px 40px 56px',
        textAlign: 'center',
      }}>
        <div style={{ display: 'inline-block', marginBottom: 20 }}>
          <AppIcon size={isMobile ? 88 : 112} />
        </div>

        <h1 style={{
          color: '#f1f5f9',
          fontSize: isMobile ? 32 : 44,
          fontWeight: 800,
          margin: '0 0 8px',
          letterSpacing: '-0.5px',
        }}>
          LYT Field
        </h1>

        <p style={{
          color: '#94a3b8',
          fontSize: isMobile ? 16 : 18,
          margin: '0 0 32px',
          maxWidth: 500,
          marginLeft: 'auto',
          marginRight: 'auto',
        }}>
          Auto-tracks your work progress. Just do your job — the app handles the rest.
        </p>

        {/* Download Button */}
        <a
          href={APK_URL}
          download="LYT-Field.apk"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 10,
            backgroundColor: '#f97316',
            color: '#fff',
            fontSize: isMobile ? 17 : 18,
            fontWeight: 700,
            padding: isMobile ? '16px 32px' : '16px 40px',
            borderRadius: 12,
            textDecoration: 'none',
            transition: 'background 0.2s',
            boxShadow: '0 4px 14px rgba(249,115,22,0.4)',
          }}
          onMouseOver={e => e.currentTarget.style.backgroundColor = '#ea580c'}
          onMouseOut={e => e.currentTarget.style.backgroundColor = '#f97316'}
        >
          <Download size={20} />
          Download for Android
        </a>

        <p style={{ color: '#64748b', fontSize: 13, marginTop: 12 }}>
          v1.0.0 · Android 8+ · 79 MB
        </p>

        {/* iOS note */}
        <p style={{ color: '#475569', fontSize: 13, marginTop: 8 }}>
          iPhone? Contact Matt for TestFlight access.
        </p>
      </div>

      {/* Features */}
      <div style={{
        maxWidth: 800,
        margin: '0 auto',
        padding: isMobile ? '32px 20px' : '48px 40px',
      }}>
        <h2 style={{
          color: darkMode ? '#f1f5f9' : '#1e293b',
          fontSize: isMobile ? 22 : 26,
          fontWeight: 700,
          textAlign: 'center',
          marginBottom: 32,
        }}>
          How It Works
        </h2>

        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
          gap: 16,
        }}>
          {features.map((f, i) => (
            <div key={i} style={{
              backgroundColor: darkMode ? '#1e293b' : '#fff',
              borderRadius: 14,
              padding: 20,
              border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 12,
                marginBottom: 10,
              }}>
                <div style={{
                  width: 40, height: 40, borderRadius: 10,
                  backgroundColor: darkMode ? '#0f172a' : '#fff7ed',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                }}>
                  <f.icon size={20} color="#f97316" />
                </div>
                <h3 style={{
                  color: darkMode ? '#f1f5f9' : '#1e293b',
                  fontSize: 16, fontWeight: 700, margin: 0,
                }}>
                  {f.title}
                </h3>
              </div>
              <p style={{
                color: darkMode ? '#94a3b8' : '#64748b',
                fontSize: 14, margin: 0, lineHeight: 1.5,
              }}>
                {f.desc}
              </p>
            </div>
          ))}
        </div>

        {/* Install Instructions (collapsible) */}
        <div style={{ marginTop: 32 }}>
          <button
            onClick={() => setShowInstructions(!showInstructions)}
            style={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              backgroundColor: darkMode ? '#1e293b' : '#fff',
              border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
              borderRadius: 12,
              padding: '16px 20px',
              cursor: 'pointer',
              color: darkMode ? '#f1f5f9' : '#1e293b',
              fontSize: 16,
              fontWeight: 600,
            }}
          >
            Installation Instructions
            {showInstructions ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
          </button>

          {showInstructions && (
            <div style={{
              backgroundColor: darkMode ? '#1e293b' : '#fff',
              border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
              borderTop: 'none',
              borderRadius: '0 0 12px 12px',
              padding: '8px 20px 20px',
            }}>
              {steps.map((s, i) => (
                <div key={i} style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 14,
                  padding: '12px 0',
                  borderBottom: i < steps.length - 1 ? `1px solid ${darkMode ? '#334155' : '#f1f5f9'}` : 'none',
                }}>
                  <div style={{
                    width: 28, height: 28, borderRadius: '50%',
                    backgroundColor: '#f97316',
                    color: '#fff',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 13, fontWeight: 700, flexShrink: 0,
                  }}>
                    {s.num}
                  </div>
                  <p style={{
                    color: darkMode ? '#cbd5e1' : '#475569',
                    fontSize: 14, margin: 0, lineHeight: 1.5, paddingTop: 3,
                  }}>
                    {s.text}
                  </p>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* What it tracks */}
        <div style={{
          marginTop: 32,
          backgroundColor: darkMode ? '#1e293b' : '#fff',
          borderRadius: 14,
          padding: 24,
          border: `1px solid ${darkMode ? '#334155' : '#e2e8f0'}`,
        }}>
          <h3 style={{
            color: darkMode ? '#f1f5f9' : '#1e293b',
            fontSize: 18, fontWeight: 700, margin: '0 0 16px',
          }}>
            What to expect
          </h3>
          {[
            'Sign in once — stays logged in',
            'Turn on tracking in Profile — runs in the background',
            'Work your segments as normal — the app detects your progress',
            'End of day: open My Day tab, review what it detected, tap "Looks Good"',
            'System learns from your confirmations and gets more accurate over time',
          ].map((item, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 10,
              padding: '8px 0',
            }}>
              <CheckCircle size={16} color="#10b981" style={{ flexShrink: 0 }} />
              <span style={{
                color: darkMode ? '#cbd5e1' : '#475569',
                fontSize: 14,
              }}>
                {item}
              </span>
            </div>
          ))}
        </div>

        {/* Footer */}
        <p style={{
          color: darkMode ? '#475569' : '#94a3b8',
          fontSize: 12,
          textAlign: 'center',
          marginTop: 40,
        }}>
          LYT Communications LLC · Internal use only
        </p>
      </div>
    </div>
  );
}
